// No repeatable add/remove-row form pattern exists elsewhere in this
// codebase (checked before building this) - genuinely new UI here, styled
// to match GreenhouseRunForm/CameraScheduleForm's card chrome.
const MAX_STEPS = 16; // mirrors firmware/esp32/src/stress_scheduler.h MAX_STEPS
const TEMP_MIN_C = 5.0;
const TEMP_MAX_C = 60.0;
const RH_MIN_PCT = 20.0;
const RH_MAX_PCT = 97.0;

const emptyStep = () => ({ kind: "hold", temp_c: 25.0, rh_pct: 60.0, duration_seconds: 3600 });

function stepError(step) {
  if (step.temp_c !== "" && step.temp_c !== null && step.temp_c !== undefined) {
    const t = Number(step.temp_c);
    if (!Number.isFinite(t) || t < TEMP_MIN_C || t > TEMP_MAX_C) {
      return `Temp must be ${TEMP_MIN_C}-${TEMP_MAX_C}°C`;
    }
  }
  if (step.rh_pct !== "" && step.rh_pct !== null && step.rh_pct !== undefined) {
    const rh = Number(step.rh_pct);
    if (!Number.isFinite(rh) || rh < RH_MIN_PCT || rh > RH_MAX_PCT) {
      return `RH must be ${RH_MIN_PCT}-${RH_MAX_PCT}%`;
    }
  }
  const duration = Number(step.duration_seconds);
  if (!Number.isFinite(duration) || duration <= 0) {
    return "Duration must be a positive number of seconds";
  }
  return null;
}

export default function StressProfileEditor({ steps, onChange }) {
  const updateStep = (index, patch) => {
    onChange(steps.map((step, i) => (i === index ? { ...step, ...patch } : step)));
  };

  const removeStep = (index) => {
    onChange(steps.filter((_, i) => i !== index));
  };

  const addStep = () => {
    if (steps.length >= MAX_STEPS) return;
    onChange([...steps, emptyStep()]);
  };

  return (
    <div className="flex flex-col gap-2">
      {steps.map((step, index) => {
        const error = stepError(step);
        return (
          <div
            key={index}
            className="grid grid-cols-2 gap-2 rounded-xl border border-slate-200 bg-slate-50 p-2.5 sm:grid-cols-5 sm:items-end"
          >
            <label className="text-xs font-semibold text-slate-600">
              Step {index + 1}
              <select
                aria-label={`Step ${index + 1} kind`}
                value={step.kind}
                onChange={(event) => updateStep(index, { kind: event.target.value })}
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm"
              >
                <option value="hold">Hold</option>
                <option value="ramp">Ramp to</option>
              </select>
            </label>
            <label className="text-xs font-semibold text-slate-600">
              Temp (°C)
              <input
                type="number"
                step="0.5"
                aria-label={`Step ${index + 1} temperature`}
                value={step.temp_c}
                onChange={(event) => updateStep(index, { temp_c: event.target.value === "" ? "" : Number(event.target.value) })}
                className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
              />
            </label>
            <label className="text-xs font-semibold text-slate-600">
              RH (%)
              <input
                type="number"
                step="1"
                aria-label={`Step ${index + 1} humidity`}
                value={step.rh_pct}
                onChange={(event) => updateStep(index, { rh_pct: event.target.value === "" ? "" : Number(event.target.value) })}
                className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
              />
            </label>
            <label className="text-xs font-semibold text-slate-600">
              Duration (s)
              <input
                type="number"
                step="1"
                min="1"
                aria-label={`Step ${index + 1} duration seconds`}
                value={step.duration_seconds}
                onChange={(event) => updateStep(index, { duration_seconds: Number(event.target.value) })}
                className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
              />
            </label>
            <button
              type="button"
              onClick={() => removeStep(index)}
              className="rounded-lg border border-red-200 bg-white px-2 py-1.5 text-xs font-bold text-red-700"
            >
              Remove
            </button>
            {error && <p className="col-span-2 text-xs text-red-600 sm:col-span-5">{error}</p>}
          </div>
        );
      })}
      <button
        type="button"
        onClick={addStep}
        disabled={steps.length >= MAX_STEPS}
        className="self-start rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 disabled:opacity-50"
      >
        + Add step
      </button>
      {steps.length >= MAX_STEPS && (
        <p className="text-xs text-slate-500">Maximum {MAX_STEPS} steps (firmware limit).</p>
      )}
    </div>
  );
}

export { MAX_STEPS, emptyStep, stepError };
